import { getEntityTypeConfig } from '@/lib/crm/record/registry/entityTypeRegistry';
import { isVisiblePropertyKey } from '@/lib/crm/record/utils/buildDetailFieldList';
import type { CrmObject } from '@/types/crm';

import type { PropertySchemaMap } from '../../hooks/usePropertySchema';
import { isMergeValueEmpty } from './useMergePropertyResolution';

/**
 * Max rows (property + association) shown on a merge candidate card. 5 rather
 * than 3-4 so a fully-populated contact keeps showing company-style rows in
 * addition to email/phone.
 */
export const MERGE_CARD_PROPERTY_CAP = 5;

export type MergeCardRow =
  | { type: 'property'; key: string }
  | { type: 'association'; key: string; targetIds: string[] };

/**
 * Derives the ordered rows to show on a merge candidate card: type-aware
 * property rows plus the association rows curated in the entity type's
 * `detailFieldOrder` (stage, company, owner, …). Without associations, types
 * like deals — whose salient fields are almost all associations — rendered
 * empty cards.
 *
 * Property visibility rules are shared with the record detail view via
 * `isVisiblePropertyKey` (buildDetailFieldList.ts) so the two surfaces can
 * never drift; only the ordering (detailFieldOrder entries, then remaining
 * schema property keys) and the merge-specific empty-value check (raw
 * `.value`, parity with CardPropertyRow) live here.
 *
 * Association rows are FAIL-CLOSED on access: a row renders only when
 * `assocAccessMap` explicitly marks the type readable (`'all'`). While the
 * association-types query is still loading the map is empty, so association
 * rows are simply absent rather than briefly leaking `internal` types
 * (mirrors the gating in overview-section). Timestamps never appear
 * (property + association entries only).
 */
export function getMergeCardRows(
  entityTypeName: string,
  schema: PropertySchemaMap,
  object: CrmObject,
  assocAccessMap: ReadonlyMap<string, string>
): MergeCardRow[] {
  const config = getEntityTypeConfig(entityTypeName);
  const hidden = new Set(config.hiddenProperties ?? []);

  const isVisibleKey = (key: string): boolean => {
    if (!isVisiblePropertyKey(key, 'read', hidden, schema)) {
      return false;
    }
    const raw = object.properties?.[key];
    if (!raw || isMergeValueEmpty(raw.value)) {
      return false;
    }
    return true;
  };

  const rows: MergeCardRow[] = [];
  const seenProperties = new Set<string>();
  const seenAssociations = new Set<string>();

  for (const field of config.detailFieldOrder ?? []) {
    if (field.type === 'property') {
      if (seenProperties.has(field.key)) {
        continue;
      }
      seenProperties.add(field.key);
      if (isVisibleKey(field.key)) {
        rows.push({ type: 'property', key: field.key });
      }
    } else if (field.type === 'association') {
      if (seenAssociations.has(field.key)) {
        continue;
      }
      seenAssociations.add(field.key);
      if (assocAccessMap.get(field.key) !== 'all') {
        continue;
      }
      const targetIds = (object.associations ?? [])
        .filter(a => a.association_type_name === field.key)
        .map(a => a.target_entity_id);
      if (targetIds.length === 0) {
        continue;
      }
      rows.push({ type: 'association', key: field.key, targetIds });
    }
  }

  for (const key of Object.keys(schema)) {
    if (seenProperties.has(key)) {
      continue;
    }
    seenProperties.add(key);
    if (isVisibleKey(key)) {
      rows.push({ type: 'property', key });
    }
  }

  return rows.slice(0, MERGE_CARD_PROPERTY_CAP);
}
